import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Mic, Square, Loader2, FileText, ArrowRight, Sparkles, AlertTriangle, Lightbulb, Camera, X, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, eur } from "@/lib/studioApi";
import { AssistantQuestions } from "./AssistantQuestions";
import { tr } from "@/i18n/appLang";

const MARKET_PRICE_QUESTION = {
  BE: "Quel est le prix de marché d'un entretien de chaudière mazout en Belgique ?",
  FR: "Quel est le prix de marché d'un entretien de chaudière gaz en France ?",
  ES: "Quel est le prix de marché d'un entretien de chaudière gaz en Espagne ?",
  LU: "Quel est le prix de marché d'un entretien de chaudière mazout au Luxembourg ?",
};
const suggestions = () => [
  "Fais un devis pour le remplacement d'une chaudière gaz à condensation 24 kW",
  MARKET_PRICE_QUESTION[localStorage.getItem("devisto_country")] || MARKET_PRICE_QUESTION.BE,
  "Rédige un SMS pour prévenir un client d'un retard de 30 minutes",
  "Quelles factures sont encore impayées ?",
];
const PHOTO_SUGGESTION = "Photographier une plaque signalétique : j'identifie le modèle et je chiffre";

const Rich = ({ text }) => (
  <>
    {text.split("\n").map((line, i) => (
      <p key={i} className={line.trim() ? "" : "h-2"}>
        {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
          part.startsWith("**") && part.endsWith("**") ? <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong> : part,
        )}
      </p>
    ))}
  </>
);

const QuoteCard = ({ data, text }) => (
  <div data-testid="assistant-quote-card" className="max-w-[88%] rounded-2xl border border-[#C9D4F4] bg-[#EEF1FC] p-4">
    <p className="text-[15px] text-[#0F2645]">{text}</p>
    <div className="mt-3 flex items-center gap-3 rounded-xl bg-white p-3">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#3A5CC8] text-white"><FileText className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-[#0F2645]">{data.number}{data.title ? ` · ${data.title}` : ""}</p>
        <p className="text-xs text-[#5C6A87]">{data.lines_count} ligne{data.lines_count > 1 ? "s" : ""} · {eur(data.total)} TVAC{data.template ? ` · modèle « ${data.template} »` : ""}</p>
      </div>
      <Link data-testid="assistant-open-quote" to={`/studio/devis/${data.quote_id}`} className="inline-flex min-h-[38px] shrink-0 items-center gap-1 rounded-xl bg-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.14)] px-3.5 text-xs font-semibold text-white hover:bg-[#2F4CA8]">
        Ouvrir <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
    {data.missing?.length > 0 && (
      <ul className="mt-3 space-y-1 text-xs text-amber-800">
        {data.missing.map((m) => <li key={m} className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{m}</li>)}
      </ul>
    )}
    {data.suggestions?.length > 0 && (
      <div className="mt-3">
        <p className="mb-1.5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#5C6A87]"><Lightbulb className="h-3 w-3 text-amber-500" /> Compléter ce devis</p>
        <div className="flex flex-wrap gap-1.5">
          {data.suggestions.slice(0, 8).map((s, i) => (
            <Link key={s} data-testid={`assistant-quote-suggestion-${i + 1}`} to={`/studio/devis/${data.quote_id}?instruction=${encodeURIComponent(s)}`} className="rounded-full border border-[#C9D4F4] bg-white px-2.5 py-1 text-xs font-medium text-[#0F2645] transition-colors hover:border-[#3A5CC8] hover:text-[#2F4CA8]">
              {s}
            </Link>
          ))}
        </div>
      </div>
    )}
  </div>
);

const Bubble = ({ m }) => {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div data-testid={m.kind === "photo" ? "assistant-user-photo" : "assistant-user-message"} className={`max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-[#3A5CC8] text-[15px] leading-relaxed text-white ${m.kind === "photo" ? "overflow-hidden p-1.5" : "px-4 py-2.5"} ${m.pending ? "opacity-70" : ""}`}>
          {m.kind === "photo" && m.data?.image && <img src={m.data.image} alt="Photo envoyée" className="max-h-64 w-full rounded-xl object-cover" />}
          {m.kind === "photo" ? (
            <div className="px-2.5 pb-1.5 pt-2">
              <p>{m.content}</p>
              {m.data?.vision && <p data-testid="assistant-vision" className="mt-1.5 flex items-start gap-1.5 text-xs text-white/85"><ScanSearch className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {m.data.vision}</p>}
            </div>
          ) : m.kind === "answers" && m.data?.answers
            ? m.data.answers.map((a, i) => <p key={i}>{i + 1} → {a.answer}</p>)
            : m.content}
        </div>
      </div>
    );
  }
  if (m.kind === "quote") return <QuoteCard data={m.data} text={m.content} />;
  return (
    <div data-testid="assistant-reply" className="max-w-[88%] text-[15px] leading-relaxed text-[#0F2645]">
      <Rich text={m.content} />
      {m.kind === "questions" && (
        <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[14px]">
          {m.data.questions.map((q, i) => (
            <li key={i}><strong className="font-semibold">{q.question}</strong> <span className="text-[#5C6A87]">({q.options.join(", ")})</span></li>
          ))}
        </ol>
      )}
    </div>
  );
};

export const AssistantChat = ({ chat, compact = false }) => {
  const { messages, loading, sending, error, send, sendPhoto } = chat;
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [dismissed, setDismissed] = useState("");
  const bottomRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);

  const last = messages[messages.length - 1];
  const pendingQuestions = last?.role === "assistant" && last.kind === "questions" && dismissed !== last.id ? last : null;

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, sending, pendingQuestions]);
  useEffect(() => () => photo && URL.revokeObjectURL(photo.url), [photo]);

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|heic|heif)/i.test(file.type) && !/\.(jpe?g|png|webp|heic)$/i.test(file.name)) return toast.error("Choisissez une photo JPEG ou PNG.");
    setPhoto({ file, url: URL.createObjectURL(file) });
  };

  const submit = async (value) => {
    const v = (value ?? text).trim();
    if (sending) return;
    if (photo) {
      const { file } = photo;
      setPhoto(null);
      setText("");
      await sendPhoto(file, v);
      return;
    }
    if (v.length < 2) return;
    setText("");
    await send(v);
  };

  const answer = async (values) => {
    const answers = pendingQuestions.data.questions.map((q, i) => ({ question: q.question, answer: values[i] }));
    setDismissed(pendingQuestions.id);
    await send(answers.map((a, i) => `${i + 1} → ${a.answer}`).join("\n"), answers);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecording(false);
        if (blob.size < 1200) return toast.error("Enregistrement trop court.");
        setTranscribing(true);
        const fd = new FormData();
        fd.append("file", new File([blob], `message.${mimeType.includes("webm") ? "webm" : "mp4"}`, { type: mimeType }));
        try {
          const { data } = await studio.post("/transcribe", fd, { timeout: 180000 });
          setText("");
          submit(data.text);
        } catch (e) {
          toast.error(apiError(e, "Transcription impossible."));
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast.error("Micro indisponible. Autorisez l'accès au micro.");
    }
  };

  const stopRecording = () => mediaRef.current?.state === "recording" && mediaRef.current.stop();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div data-testid="assistant-messages" className={`flex-1 space-y-4 overflow-y-auto px-4 ${compact ? "py-4" : "py-6"}`}>
        {loading && <div className="flex justify-center py-10 text-[#93A0BC]"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {!loading && messages.length === 0 && (
          <div data-testid="assistant-empty" className="mx-auto max-w-md pt-6 text-center">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF1FC] text-[#3A5CC8]"><Sparkles className="h-6 w-6" /></span>
            <h2 className="font-heading mt-4 text-lg font-bold text-[#0F2645]">Que puis-je faire pour vous ?</h2>
            <p className="mt-1 text-sm text-[#5C6A87]">Dictez un chantier pour obtenir un devis, envoyez la photo d'une plaque signalétique, posez une question technique ou demandez un texte pour un client. Je garde le fil de chaque conversation.</p>
            <div className="mt-5 grid gap-2 text-left">
              {suggestions().map((s, i) => (
                <button key={s} type="button" data-testid={`assistant-suggestion-${i + 1}`} onClick={() => submit(tr(s))} className="rounded-xl border border-[#E2E7F7] bg-white px-3.5 py-2.5 text-sm text-[#0F2645] transition-colors hover:border-[#C9D4F4] hover:bg-[#F6F8FD]">
                  {s}
                </button>
              ))}
              <button type="button" data-testid="assistant-suggestion-photo" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-xl border border-dashed border-[#C9D4F4] bg-[#F6F8FD] px-3.5 py-2.5 text-sm text-[#2F4CA8] transition-colors hover:border-[#3A5CC8] hover:bg-[#EEF1FC]">
                <Camera className="h-4 w-4 shrink-0" /> {PHOTO_SUGGESTION}
              </button>
            </div>
          </div>
        )}
        {messages.map((m) => <Bubble key={m.id} m={m} />)}
        {sending && (
          <div data-testid="assistant-typing" className="flex items-center gap-2 text-sm text-[#5C6A87]">
            <span className="flex gap-1">
              {[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#93A0BC]" style={{ animationDelay: `${i * 120}ms` }} />)}
            </span>
            {last?.kind === "answers" ? "Génération du devis" : "L'assistant réfléchit"}
          </div>
        )}
        {error && <p data-testid="assistant-error" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        <div ref={bottomRef} />
      </div>

      <div className="shrink-0 px-3 pb-3 pt-1">
        {pendingQuestions ? (
          <AssistantQuestions key={pendingQuestions.id} questions={pendingQuestions.data.questions} onSubmit={answer} onDismiss={() => setDismissed(pendingQuestions.id)} busy={sending} />
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); submit(); }}
            className="rounded-2xl border border-[#E2E7F7] bg-white p-2 shadow-[0_4px_20px_-8px_rgba(15,38,69,0.15)] focus-within:border-[#3A5CC8]"
          >
            {photo && (
              <div data-testid="assistant-photo-preview" className="mb-2 flex items-center gap-3 rounded-xl bg-[#F6F8FD] p-2">
                <img src={photo.url} alt="Aperçu" className="h-14 w-14 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#0F2645]">{photo.file.name}</p>
                  <p className="text-xs text-[#5C6A87]">Ajoutez une consigne (facultatif) puis envoyez : l'assistant identifie le matériel.</p>
                </div>
                <button type="button" data-testid="assistant-photo-remove" onClick={() => setPhoto(null)} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#93A0BC] hover:bg-white hover:text-rose-600" aria-label="Retirer la photo"><X className="h-4 w-4" /></button>
              </div>
            )}
            <div className="flex items-end gap-1.5">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="assistant-photo-input" onChange={pickPhoto} />
            <button
              type="button"
              data-testid="assistant-photo-btn"
              onClick={() => fileRef.current?.click()}
              disabled={sending || transcribing}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#5C6A87] transition-colors hover:bg-[#F3F5FC] disabled:opacity-40"
              aria-label="Envoyer une photo"
            >
              <Camera className="h-5 w-5" />
            </button>
            <textarea
              data-testid="assistant-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
              rows={1}
              placeholder={transcribing ? "Transcription en cours…" : photo ? "Ex. : chiffre le remplacement de cette chaudière" : "Envoyer un message…"}
              disabled={transcribing}
              className="max-h-40 min-h-[44px] flex-1 resize-none bg-transparent px-2 py-2.5 text-[15px] text-[#0F2645] placeholder:text-[#93A0BC] focus:outline-none"
              style={{ height: "auto" }}
              onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`; }}
            />
            <button
              type="button"
              data-testid="assistant-mic"
              onClick={recording ? stopRecording : startRecording}
              disabled={sending || transcribing}
              className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors ${recording ? "bg-rose-500 text-white" : "text-[#5C6A87] hover:bg-[#F3F5FC]"}`}
              aria-label={recording ? "Arrêter la dictée" : "Dicter"}
            >
              {transcribing ? <Loader2 className="h-5 w-5 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
            </button>
            <button
              type="submit"
              data-testid="assistant-send"
              disabled={sending || (!photo && text.trim().length < 2)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#3A5CC8] text-white hover:bg-[#2F4CA8] disabled:opacity-40"
              aria-label="Envoyer"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUp className="h-5 w-5" />}
            </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
